/**
 * A way of speaking to one person, without saying how.
 *
 * On our machine that is a WebSocket. On a player's machine - which is where
 * worlds are going - it will be a data channel between two browsers. The
 * game's own code should not be able to tell the difference, and after this
 * it cannot: everything it may do to a person is here, and it is four things.
 *
 * `send` and `sendChunk` are already what a `Realm` is allowed to know about
 * somebody (see `RealmClient`), which is the half of this that was done
 * before anybody planned it. What this adds is closing a link and asking
 * whether it is still there, so that the part which *routes* messages can
 * stop naming WebSocket as well.
 */
export interface Link {
  /** One ordinary message. Encoded by whoever owns the link. */
  send(message: object): void;
  /**
   * One message already written out, for a snapshot that goes to many.
   *
   * A link over a wire sends the text and is spared the encoding; one in
   * the same process hands over the object. See core/net/snapshot.
   */
  sendEncoded(message: object, text: string): void;
  /** One chunk of world, as bytes. These are most of what a link carries. */
  sendChunk(frame: ArrayBuffer): void;
  /** Is it still open? A closed link silently swallows everything. */
  open(): boolean;
  /**
   * Shut it.
   *
   * With a reason where the far end can be told one - a browser reads the
   * code and the words off a WebSocket close, and a data channel has its own
   * way of saying it. Nobody who calls this cares which.
   */
  close(code?: number, why?: string): void;
}

/**
 * A link that remembers instead of sending.
 *
 * For checks: what the game said to somebody is a list, and asserting
 * against a list beats standing up a socket to listen on.
 *
 * Everything it keeps is an array, deliberately - so that the object can be
 * spread into a bigger one (a `RealmClient` needs a name and a position as
 * well as a way of being spoken to) and still be watched afterwards. A
 * getter would have been read once by the spread and frozen at its answer.
 */
export interface HeardLink extends Link {
  readonly heard: object[];
  readonly chunks: ArrayBuffer[];
  /** Empty while it is open; one entry, with whatever was said, once shut. */
  readonly goodbyes: { code?: number; why?: string }[];
}

export function heardLink(): HeardLink {
  const heard: object[] = [];
  const chunks: ArrayBuffer[] = [];
  const goodbyes: { code?: number; why?: string }[] = [];
  const open = (): boolean => goodbyes.length === 0;
  return {
    heard, chunks, goodbyes, open,
    send: (message) => { if (open()) heard.push(message); },
    sendEncoded: (message) => { if (open()) heard.push(message); },
    sendChunk: (frame) => { if (open()) chunks.push(frame); },
    // Only the first hang-up is recorded: closing twice is one departure.
    close: (code, why) => {
      if (!open()) return;
      goodbyes.push({
        ...(code === undefined ? {} : { code }),
        ...(why === undefined ? {} : { why }),
      });
    },
  };
}

/**
 * A link with a far end that answers.
 *
 * A `Link` only speaks. That is all the game needs, because on our server
 * the listening belongs to whatever holds the socket. A world held in a
 * browser has no such whatever: the page is both ends of the arrangement,
 * so it needs the one object to do both, and `Wire` is that.
 *
 * Two of these make a data channel, and two of these joined to each other
 * make the loop a host's own player walks in on - which is how somebody
 * hosting a world plays in it without a single line of special treatment.
 */
export interface Wire extends Link {
  /** What arrives from the far end. Set once, by whoever owns this end. */
  onMessage: ((message: object) => void) | null;
  onChunk: ((frame: ArrayBuffer) => void) | null;
  /** And when it goes. */
  onClose: (() => void) | null;
}

/**
 * Two wires, each the other's far end.
 *
 * What somebody hosting a world talks to their own world down. No network,
 * no encoding, no copy: the message object goes straight across, because
 * both ends are the same page and pretending otherwise would be ceremony.
 *
 * **Delivered later, never in the same breath.** A real channel always
 * arrives after the send returns, and code written against one relies on
 * that without saying so. Handing a message straight to the far end would
 * re-enter the router in the middle of itself - `route` sending to a guest
 * whose handler calls `route` - and the first time that happened it would
 * be a bug nobody could read.
 */
export function wirePair(): readonly [Wire, Wire] {
  const ends: Wire[] = [];
  let shut = false;
  const make = (far: () => Wire): Wire => {
    const end: Wire = {
      onMessage: null, onChunk: null, onClose: null,
      open: () => !shut,
      /*
       * Sent now, delivered in a moment - and delivered even if this end
       * hangs up in between.
       *
       * That last part is not a nicety. Half the refusals in the game are
       * "here is why" followed immediately by a hang-up, and a wire that
       * dropped the queued message when the close came would deliver the
       * silence and not the reason. It did, the first time this was
       * written: a stranger with the wrong word heard nothing at all.
       */
      send(message: object): void {
        if (shut) return;
        queueMicrotask(() => far().onMessage?.(message));
      },
      // The same process: the object is the message, the text is spare.
      sendEncoded(message: object): void {
        if (shut) return;
        queueMicrotask(() => far().onMessage?.(message));
      },
      sendChunk(frame: ArrayBuffer): void {
        if (shut) return;
        queueMicrotask(() => far().onChunk?.(frame));
      },
      close(): void {
        if (shut) return;
        shut = true;
        // Queued behind whatever was already sent, so the last words go
        // out before the door does. Both ends, because one end of a loop
        // closing is the loop closing.
        for (const e of ends) queueMicrotask(() => e.onClose?.());
      },
    };
    return end;
  };
  const a = make(() => ends[1] as Wire);
  const b = make(() => ends[0] as Wire);
  ends.push(a, b);
  return [a, b] as const;
}
